import {
    useCallback,
    useEffect,
    Fragment,
    useState,
    useMemo,
    useRef
} from "react";
import {
    ScrollView,
    Switch,
    View
} from "react-native";
import type {
    SendNotificationModalProps,
    RedirectType
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitSnackBar,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    TextAreaInput,
    SelectBox,
    TextInput,
    Dialog,
    Button,
    Modal,
    Text
} from "ncore-ui-kit";
import type {
    GetAllUsersForAdminObjectType
} from "../../../../services/restAPI/actions/getAllUsersForAdmin/type";
import RESTServiceContext from "../../../../services/restAPI/context";

const SendNotificationModal = ({
    initialUserIds,
    isVisible,
    onSuccess,
    onClose
}: SendNotificationModalProps) => {
    const {
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const [
        title,
        setTitle
    ] = useState<string>("");

    const [
        description,
        setDescription
    ] = useState<string>("");

    const [
        imageUrl,
        setImageUrl
    ] = useState<string>("");

    const [
        redirectType,
        setRedirectType
    ] = useState<RedirectType>("none");

    const [
        selectedTargetID,
        setSelectedTargetID
    ] = useState<string>("");

    const [
        customUrl,
        setCustomUrl
    ] = useState<string>("");

    const [
        targetItems,
        setTargetItems
    ] = useState<{ __key: string; __title: string }[]>([]);

    const [
        isLoadingTargets,
        setIsLoadingTargets
    ] = useState<boolean>(false);

    const [
        sendToAll,
        setSendToAll
    ] = useState<boolean>(false);

    const [
        selectedUserIDs,
        setSelectedUserIDs
    ] = useState<string[]>([]);

    const [
        users,
        setUsers
    ] = useState<GetAllUsersForAdminObjectType[]>([]);

    const [
        isSubmitting,
        setIsSubmitting
    ] = useState<boolean>(false);

    const [
        isConfirmDialogVisible,
        setIsConfirmDialogVisible
    ] = useState<boolean>(false);

    const targetSelectBoxRef = useRef<React.ElementRef<typeof SelectBox>>(null);
    const selectBoxRef = useRef<React.ElementRef<typeof SelectBox>>(null);
    const prevUsersRef = useRef<GetAllUsersForAdminObjectType[]>([]);
    const lastFetchedSearchTextRef = useRef<string | null>(null);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const fetchUsers = useCallback((searchText?: string, isImmediate = false) => {
        if(searchText !== undefined && searchText === lastFetchedSearchTextRef.current) {
            return Promise.resolve();
        }
        lastFetchedSearchTextRef.current = searchText || "";

        if(searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        const runFetch = async (resolve: () => void) => {
            try {
                const requestParams: Record<string, unknown> = {
                    page: 1
                };
                if(searchText) {
                    requestParams.search = searchText;
                }

                const result = await RESTServiceContext.action("getAllUsersForAdmin", requestParams);
                if(result.payload) {
                    setUsers(result.payload);
                }
            } catch(error) {
                console.error(error);
            } finally {
                resolve();
            }
        };

        if(isImmediate) {
            return new Promise<void>((resolve) => {
                runFetch(resolve);
            });
        }

        return new Promise<void>((resolve) => {
            searchTimeoutRef.current = setTimeout(() => {
                runFetch(resolve);
            }, 300);
        });
    }, []);

    const fetchTargets = useCallback(async (type: RedirectType) => {
        if(type === "none" || type === "url") {
            setTargetItems([]);
            return;
        }

        try {
            setIsLoadingTargets(true);
            if(type === "news") {
                const result = await RESTServiceContext.action("getAllNews", {
                    page: 1
                });
                if(result.payload && Array.isArray(result.payload)) {
                    const items = result.payload.map((news) => ({
                        __title: news.title || news.data?.[0]?.title || news._id,
                        __key: news._id
                    }));
                    setTargetItems(items);
                }
                return;
            }

            if(type === "announcement") {
                const result = await RESTServiceContext.action("getAllAnnouncements", {
                    page: 1
                });
                if(result.payload && Array.isArray(result.payload)) {
                    const items = result.payload.map((announcement) => ({
                        __title: announcement.data?.[0]?.title || announcement._id,
                        __key: announcement._id
                    }));
                    setTargetItems(items);
                }
                return;
            }

            if(type === "event") {
                const result = await RESTServiceContext.action("getAllEventsForAdmin", null);
                if(result.payload && Array.isArray(result.payload)) {
                    const items = result.payload.map((event) => ({
                        __title: event.title || event.eventID,
                        __key: event.eventID
                    }));
                    setTargetItems(items);
                }
                return;
            }

            if(type === "form") {
                const result = await RESTServiceContext.action("getAllFormsForAdmin", null);
                if(result.payload && Array.isArray(result.payload)) {
                    const items = result.payload.map((form) => ({
                        __title: form.title || form.id,
                        __key: form.id
                    }));
                    setTargetItems(items);
                }
                return;
            }
        } catch(error) {
            console.error(error);
            setTargetItems([]);
        } finally {
            setIsLoadingTargets(false);
        }
    }, []);

    const selectBoxData = useMemo(() => {
        return users.map((user) => {
            const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
            const title = fullName ? (user.mail ? `${fullName} (${user.mail})` : fullName) : user.mail || user._id;

            return {
                __key: user._id,
                __title: title
            };
        });
    }, [
        users
    ]);

    const redirectTypeOptions = useMemo(() => [
        {
            __title: localize("notifications-redirect-none"),
            __key: "none"
        },
        {
            __title: localize("notifications-redirect-news"),
            __key: "news"
        },
        {
            __title: localize("notifications-redirect-announcement"),
            __key: "announcement"
        },
        {
            __title: localize("notifications-redirect-event"),
            __key: "event"
        },
        {
            __title: localize("notifications-redirect-form"),
            __key: "form"
        },
        {
            __title: localize("notifications-redirect-url"),
            __key: "url"
        }
    ], [
        localize
    ]);

    const initialSelectedItems = useMemo(() => {
        return selectedUserIDs.map((id) => {
            const user = users.find((u) => u._id === id);
            return {
                __title: user ? `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.mail || id : id,
                __key: id
            };
        });
    }, [
        selectedUserIDs,
        users
    ]);

    useEffect(() => {
        if(users !== prevUsersRef.current && selectBoxRef.current) {
            selectBoxRef.current.updateData(selectBoxData, initialSelectedItems);
            prevUsersRef.current = users;
        }
    }, [
        initialSelectedItems,
        selectBoxData,
        users
    ]);

    useEffect(() => {
        if(targetSelectBoxRef.current) {
            targetSelectBoxRef.current.setIsLoading(isLoadingTargets);
            if(targetItems.length > 0) {
                targetSelectBoxRef.current.updateData(targetItems, []);
            }
        }
    }, [
        isLoadingTargets,
        targetItems
    ]);

    useEffect(() => {
        if(!isVisible) {
            setTitle("");
            setDescription("");
            setImageUrl("");
            setSendToAll(false);
            setSelectedUserIDs(initialUserIds || []);
            setUsers([]);
            lastFetchedSearchTextRef.current = null;
            prevUsersRef.current = [];
            setRedirectType("none");
            setSelectedTargetID("");
            setCustomUrl("");
            setTargetItems([]);
        } else {
            lastFetchedSearchTextRef.current = null;
            fetchUsers("", true);
            if(initialUserIds && initialUserIds.length > 0) {
                setSelectedUserIDs(initialUserIds);
            }
        }
    }, [
        initialUserIds,
        fetchUsers,
        isVisible
    ]);

    const handleSendAction = async () => {
        if(title.trim().length < 2) {
            NCoreUIKitSnackBar.open({
                title: localize("notifications-validation-title")
            });
            return;
        }

        if(description.trim().length < 2) {
            NCoreUIKitSnackBar.open({
                title: localize("notifications-validation-desc")
            });
            return;
        }

        if(!sendToAll && selectedUserIDs.length === 0) {
            NCoreUIKitSnackBar.open({
                title: localize("notifications-validation-user")
            });
            return;
        }

        if(redirectType === "url" && customUrl.trim().length === 0) {
            NCoreUIKitSnackBar.open({
                title: localize("notifications-redirect-validation-url")
            });
            return;
        }

        if(redirectType !== "none" && redirectType !== "url" && !selectedTargetID) {
            NCoreUIKitSnackBar.open({
                title: localize("notifications-redirect-validation-target")
            });
            return;
        }

        try {
            setIsSubmitting(true);
            const payload: {
                data?: Record<string, unknown>;
                description: string;
                sendToAll: boolean;
                userIds?: string[];
                imageUrl?: string;
                title: string;
            } = {
                description: description.trim(),
                sendToAll: sendToAll,
                title: title.trim()
            };

            if(imageUrl.trim().length > 0) {
                payload.imageUrl = imageUrl.trim();
            }

            if(!sendToAll) {
                payload.userIds = selectedUserIDs;
            }

            let notificationData: Record<string, unknown> | undefined;

            if(redirectType === "news" && selectedTargetID) {
                notificationData = {
                    newsID: selectedTargetID,
                    redirectType: "news"
                };
            } else if(redirectType === "announcement" && selectedTargetID) {
                notificationData = {
                    announcementID: selectedTargetID,
                    redirectType: "announcement"
                };
            } else if(redirectType === "event" && selectedTargetID) {
                const selectedEvent = targetItems.find((item) => item.__key === selectedTargetID);
                notificationData = {
                    title: selectedEvent?.__title || "",
                    eventID: selectedTargetID,
                    redirectType: "event"
                };
            } else if(redirectType === "form" && selectedTargetID) {
                notificationData = {
                    formID: selectedTargetID,
                    redirectType: "form"
                };
            } else if(redirectType === "url" && customUrl.trim().length > 0) {
                notificationData = {
                    url: customUrl.trim(),
                    redirectType: "url"
                };
            }

            if(notificationData) {
                payload.data = notificationData;
            }

            const response = await RESTServiceContext.action("sendCustomNotification", payload);

            if(response && !response.isError) {
                NCoreUIKitSnackBar.open({
                    title: localize("notifications-success-sent")
                });
                onSuccess();
                onClose();
            } else {
                NCoreUIKitSnackBar.open({
                    title: response?.message || localize("notifications-error-sent")
                });
            }
        } catch(error) {
            console.error(error);
            NCoreUIKitSnackBar.open({
                title: localize("notifications-error-sent")
            });
        } finally {
            setIsSubmitting(false);
            setIsConfirmDialogVisible(false);
        }
    };

    const handlePreSubmit = () => {
        if(title.trim().length < 2) {
            NCoreUIKitSnackBar.open({
                title: localize("notifications-validation-title")
            });
            return;
        }

        if(description.trim().length < 2) {
            NCoreUIKitSnackBar.open({
                title: localize("notifications-validation-desc")
            });
            return;
        }

        if(!sendToAll && selectedUserIDs.length === 0) {
            NCoreUIKitSnackBar.open({
                title: localize("notifications-validation-user")
            });
            return;
        }

        if(redirectType === "url" && customUrl.trim().length === 0) {
            NCoreUIKitSnackBar.open({
                title: localize("notifications-redirect-validation-url")
            });
            return;
        }

        if(redirectType !== "none" && redirectType !== "url" && !selectedTargetID) {
            NCoreUIKitSnackBar.open({
                title: localize("notifications-redirect-validation-target")
            });
            return;
        }

        if(sendToAll) {
            setIsConfirmDialogVisible(true);
            return;
        }

        handleSendAction();
    };

    return <Fragment>
        <Modal
            id="send-notification-modal"
            portalName="modal-system"
            isActive={isVisible}
            onClosed={onClose}
        >
            <View
                style={[
                    stylesheet.container,
                    {
                        backgroundColor: colors.project.brandWhite,
                        borderRadius: radiuses.lg,
                        padding: spaces.spacingXl
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.headerContainer,
                        {
                            marginBottom: spaces.spacingLg
                        }
                    ]}
                >
                    <Text
                        customColor={colors.content.text.high}
                        variant="titleLargeSize"
                    >
                        {localize("notifications-new-notification")}
                    </Text>
                </View>
                <ScrollView
                    style={stylesheet.contentContainer}
                    showsVerticalScrollIndicator={true}
                >
                    <View
                        style={[
                            stylesheet.formContainer,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <TextInput
                            placeholder={localize("notifications-send-title-placeholder")}
                            title={localize("notifications-send-title-input")}
                            customColor={colors.project.inputBackground}
                            onChangeText={(val) => setTitle(val)}
                            spreadBehaviour="stretch"
                            value={title}
                        />
                        <TextAreaInput
                            key={`notification-desc-input-${isVisible ? "visible" : "hidden"}`}
                            placeholder={localize("notifications-send-desc-placeholder")}
                            title={localize("notifications-send-desc-input")}
                            customColor={colors.project.inputBackground}
                            onChangeText={(val) => setDescription(val)}
                            isUpdateOnRealtime={true}
                            spreadBehaviour="stretch"
                            value={description}
                            minHeight={100}
                        />
                        <TextInput
                            title={localize("notifications-send-image-input")}
                            customColor={colors.project.inputBackground}
                            placeholder="https://example.com/image.jpg"
                            onChangeText={(val) => setImageUrl(val)}
                            spreadBehaviour="stretch"
                            value={imageUrl}
                        />
                        <View
                            style={stylesheet.selectBoxContainer}
                        >
                            <SelectBox
                                titleExtractor={(item) => (item as { __title?: string }).__title || ""}
                                keyExtractor={(item) => (item as { __key?: string }).__key || ""}
                                key={`redirect-type-select-${isVisible ? "visible" : "hidden"}`}
                                onChange={(selectedItems) => {
                                    const item = selectedItems[0];
                                    const newType = (item?.__key || "none") as RedirectType;
                                    setRedirectType(newType);
                                    setSelectedTargetID("");
                                    fetchTargets(newType);
                                }}
                                initialSelectedItems={[
                                    {
                                        __title: localize("notifications-redirect-none"),
                                        __key: "none"
                                    }
                                ]}
                                placeholder={localize("notifications-redirect-none")}
                                title={localize("notifications-redirect-type-label")}
                                selectSheetProps={{
                                    portalName: "modal-system"
                                }}
                                data={redirectTypeOptions}
                                spreadBehaviour="stretch"
                                isMultipleSelect={false}
                                isSearchable={false}
                            />
                        </View>
                        {
                            redirectType !== "none" && redirectType !== "url" ?
                                <View
                                    style={stylesheet.selectBoxContainer}
                                >
                                    <SelectBox
                                        key={`redirect-target-${redirectType}-${isVisible ? "visible" : "hidden"}`}
                                        titleExtractor={(item) => (item as { __title?: string }).__title || ""}
                                        placeholder={localize("notifications-redirect-target-placeholder")}
                                        keyExtractor={(item) => (item as { __key?: string }).__key || ""}
                                        onChange={(selectedItems) => {
                                            const item = selectedItems[0];
                                            setSelectedTargetID(item ? (item.__key as string) : "");
                                        }}
                                        title={localize("notifications-redirect-target-select")}
                                        selectSheetProps={{
                                            portalName: "modal-system"
                                        }}
                                        spreadBehaviour="stretch"
                                        isMultipleSelect={false}
                                        ref={targetSelectBoxRef}
                                        isSearchable={true}
                                        data={targetItems}
                                    />
                                </View>
                            :
                                null
                        }
                        {
                            redirectType === "url" ?
                                <TextInput
                                    placeholder={localize("notifications-redirect-url-placeholder")}
                                    title={localize("notifications-redirect-url-input")}
                                    customColor={colors.project.inputBackground}
                                    onChangeText={(val) => setCustomUrl(val)}
                                    spreadBehaviour="stretch"
                                    value={customUrl}
                                />
                            :
                                null
                        }
                        <View
                            style={[
                                stylesheet.switchRowContainer,
                                {
                                    backgroundColor: colors.project.inputBackground,
                                    borderRadius: radiuses.sm,
                                    padding: spaces.spacingMd
                                }
                            ]}
                        >
                            <View
                                style={[
                                    stylesheet.switchTextContainer,
                                    {
                                        marginRight: spaces.spacingMd
                                    }
                                ]}
                            >
                                <Text
                                    customColor={colors.content.text.high}
                                    variant="bodyMediumSize"
                                >
                                    {localize("notifications-send-to-all")}
                                </Text>
                                <Text
                                    customColor={colors.content.text.mid}
                                    variant="bodySmallSize"
                                >
                                    {localize("notifications-send-to-all-desc")}
                                </Text>
                            </View>
                            <Switch
                                onValueChange={(val) => setSendToAll(val)}
                                value={sendToAll}
                            />
                        </View>
                        {
                            !sendToAll ?
                                <View
                                    style={[
                                        stylesheet.selectBoxContainer,
                                        {
                                            gap: spaces.spacingXs
                                        }
                                    ]}
                                >
                                    <SelectBox
                                        onChange={(selectedItems) => {
                                            setSelectedUserIDs(selectedItems.map((item) => item.__key as string));
                                        }}
                                        titleExtractor={(item) => (item as { __title?: string }).__title || ""}
                                        keyExtractor={(item) => (item as { __key?: string }).__key || ""}
                                        onSearch={(props) => {
                                            props.setIsLoading(true);
                                            fetchUsers(props.searchText).finally(() => {
                                                props.setIsLoading(false);
                                            });

                                            if(!props.searchText) {
                                                return props.data;
                                            }

                                            const searchLower = props.searchText.toLowerCase();
                                            return props.data.filter((item) =>
                                                (item.__title || "").toLowerCase().includes(searchLower)
                                            );
                                        }}
                                        placeholder={localize("notifications-send-select-placeholder")}
                                        title={localize("notifications-send-select-users")}
                                        initialSelectedItems={initialSelectedItems}
                                        key="send-notification-users-selectbox"
                                        selectSheetProps={{
                                            portalName: "modal-system"
                                        }}
                                        isShowSelectSheetTools={true}
                                        spreadBehaviour="stretch"
                                        isMultipleSelect={true}
                                        data={selectBoxData}
                                        isSearchable={true}
                                        ref={selectBoxRef}
                                    />
                                    <Text
                                        customColor={colors.content.text.mid}
                                        variant="bodySmallSize"
                                    >
                                        {`${localize("notifications-send-selected-count")} ${selectedUserIDs.length}`}
                                    </Text>
                                </View>
                            :
                                null
                        }
                    </View>
                </ScrollView>
                <View
                    style={[
                        stylesheet.footerContainer,
                        {
                            marginTop: spaces.spacingMd,
                            gap: spaces.spacingSm
                        }
                    ]}
                >
                    <Button
                        title={localize("cancel")}
                        isDisabled={isSubmitting}
                        spreadBehaviour="free"
                        onPress={onClose}
                        variant="outline"
                        type="neutral"
                    />
                    <Button
                        title={localize("notifications-send-notification")}
                        onPress={handlePreSubmit}
                        isDisabled={isSubmitting}
                        isLoading={isSubmitting}
                        spreadBehaviour="free"
                        variant="filled"
                        type="primary"
                    />
                </View>
            </View>
        </Modal>
        <Dialog
            content={localize("notifications-send-confirm-content")}
            primaryButtonProps={{
                title: localize("notifications-send-confirm-yes"),
                onPress: handleSendAction
            }}
            secondaryButtonProps={{
                onPress: () => setIsConfirmDialogVisible(false),
                title: localize("cancel")
            }}
            isVisible={isConfirmDialogVisible}
            variant="yes-no"
        />
    </Fragment>;
};
export default SendNotificationModal;
